// src/utils/lazyWithPreload.js
// Lazy pages that are downloaded in the background right after login, so opening a page later is instant.
//
// Why pages became slow after lazy loading: React.lazy downloads a page's code only when you first open it,
// so every first click on a menu item waits for a download. The login page got faster, but every page got slower.
// This keeps the fast first load AND makes later pages instant:
//   1. Login / first screen: only the code it needs is downloaded (same as now).
//   2. After login, when the browser is idle: every other page's code is downloaded quietly, one by one.
//   3. Hovering a menu item downloads that page at once (if it isn't already).
//
// Usage (App.js):
//   import { lazyWithPreload, preloadAllPages } from "./utils/lazyWithPreload";
//   const Dashboard = lazyWithPreload(() => import("./components/modules/Dashboard").then((m) => ({ default: m.Dashboard })), "dashboard");
//   ...after login succeeds:  preloadAllPages();
// Sidebar (optional): onMouseEnter={() => preloadPage("dashboard")}

import { lazy } from "react";

const pages = new Map();          // name → load()
const RELOADED_KEY = "chunk_reload_once";

// After a new deploy, old page files no longer exist on the server ("ChunkLoadError").
// Reload the site once to get the new files instead of showing a blank page.
function withDeployRetry(factory) {
  return () =>
    factory()
      .then((m) => {
        try { sessionStorage.removeItem(RELOADED_KEY); } catch { /* storage blocked */ }
        return m;
      })
      .catch((err) => {
        const isChunkError = /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i
          .test(String(err && (err.name + " " + err.message)));
        let reloaded = false;
        try { reloaded = sessionStorage.getItem(RELOADED_KEY) === "1"; } catch { /* storage blocked */ }
        if (isChunkError && !reloaded) {
          try { sessionStorage.setItem(RELOADED_KEY, "1"); } catch { /* storage blocked */ }
          window.location.reload();
          return new Promise(() => {});   // keep waiting while the page reloads
        }
        throw err;
      });
}


export function lazyWithPreload(factory, name) {
  let promise = null;
  const load = () => {
    if (!promise) promise = withDeployRetry(factory)().catch((err) => { promise = null; throw err; });
    return promise;
  };
  const Component = lazy(load);
  Component.preload = load;
  pages.set(name || `page${pages.size}`, load);
  return Component;
}

/** Download one page's code now (e.g. on menu hover). Safe to call many times. */
export function preloadPage(name) {
  const load = pages.get(name);
  if (load) load().catch(() => { /* real error shows when the page is opened */ });
}

const idle = (fn) =>
  typeof window !== "undefined" && window.requestIdleCallback
    ? window.requestIdleCallback(fn, { timeout: 3000 })
    : setTimeout(fn, 300);

let started = false;

export function preloadAllPages({ delayMs = 0 } = {}) {
  if (started) return;
  started = true;
  // Slow / data-saver connections: don't download everything, pages still load on click
  const conn = typeof navigator !== "undefined" && navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ""))) return;

  setTimeout(() => idle(() => {
    pages.forEach((load) => load().catch(() => { /* the real error shows when that page is opened */ }));
  }), delayMs);
}